import React from 'react';import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';

// ─── DATA ─────────────────────────────────────────────────────────────────────
const chartData = [
  { year: '2022', l1: 2.18, trend: 2.20 },
  { year: '2023', l1: 1.95, trend: 1.97 },
  { year: '2024', l1: 2.05, trend: 2.06 },
  { year: '2025', l1: 1.78, trend: 1.80 },
  { year: '2025 (Proj)', l1: 1.85, trend: 1.82, isProjection: true },
];

// ─── CUSTOM Y-AXIS TICK ───────────────────────────────────────────────────────
const CustomYTick = ({ x, y, payload }) => (
  <text x={x} y={y} dy={4} textAnchor="end" fill="#9ca3af" fontSize={11}>
    ₹{payload.value.toFixed(2)}Cr
  </text>
);

// ─── CUSTOM DOT ───────────────────────────────────────────────────────────────
const CustomDot = (props) => {
  const { cx, cy } = props;
  return (
    <circle cx={cx} cy={cy} r={5} fill="#fff" stroke="#3b82f6" strokeWidth={2} />
  );
};

// ─── CUSTOM TOOLTIP ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px', fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color }}>
            {p.name}: ₹{p.value} Cr
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// ─── STAT CARD ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, badge }) => (
  <div style={{
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '20px 24px',
    flex: 1,
    minWidth: 0,
  }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'none', letterSpacing: '0.06em', marginBottom: 10 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', marginBottom: 6, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 10, color: '#9ca3af' }}>{sub}</div>
    {badge && (
      <div style={{ marginTop: 8, fontSize: 11, fontWeight: 600, color: '#16a34a' }}>▲ {badge}</div>
    )}
  </div>
);

// ─── SUMMARY ROW ──────────────────────────────────────────────────────────────
const SummaryRow = ({ label, value, highlighted }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '13px 14px',
    borderRadius: highlighted ? 8 : 0,
    background: highlighted ? '#ccfbf1' : 'transparent',
    borderBottom: highlighted ? 'none' : '1px solid #f3f4f6',
    margin: highlighted ? '4px 0' : 0,
  }}>
    <span style={{ fontSize: 13, color: highlighted ? '#0f766e' : '#6b7280' }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 700, color: highlighted ? '#0f766e' : '#111827' }}>{value}</span>
  </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const Projection = () => {
  return (
    <div style={{
      minWidth: 1024,
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      color: '#111827',
      padding: '0 4px',
      background: '#fff',
    }}>

      {/* ── TOP HEADER ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <button style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280', padding: 0, lineHeight: 1 }}>←</button>
            <h4 style={{ margin: 0, fontWeight: 700, fontSize: 22, color: '#111827' }}>Fabrication & Supply of Control Panels</h4>
          </div>
          <div style={{ fontSize: 13, color: '#9ca3af', marginLeft: 28 }}>BEL · Ministry of Defence · Karnataka</div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <span style={{ background: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20 }}>Awarded</span>
          <span style={{ background: '#dcfce7', color: '#166534', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20 }}>Highly Recommended</span>
        </div>
      </div>

      {/* ── STAT CARDS ROW ── */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Tender Value" value="₹2.40 Cr" sub="Estimated" />
        <StatCard label="Past Avg L1" value="₹2.09 Cr" sub="Historical average" />
        <StatCard
          label="Projected L1"
          value="₹1.92 Cr"
          sub="AI projection (2025)"
          badge="8% below estimate"
        />
        <StatCard label="Bidders (Hist.)" value="12" sub="Expected competitors" />
        <StatCard
          label="Win Probability"
          value="41.8%"
          sub="Based on history"
          badge="Above platform avg"
        />
      </div>

      {/* ── BOTTOM ROW: CHART + SUMMARY ── */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>

        {/* Chart Card */}
        <div style={{
          flex: '1 1 65%',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: '24px 28px 20px',
       height:'fit-content'
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 4 }}>Historical L1 Bid Trend & 2025 Projection</div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 20 }}>Based on past bid data</div>

   <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }} barGap={0}>
              <CartesianGrid strokeDasharray="" vertical={false} stroke="#f3f4f6" />
              <XAxis
                dataKey="year"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#9ca3af' }}
                dy={10}
              />
              <YAxis
                tick={<CustomYTick />}
                axisLine={false}
                tickLine={false}
                domain={[0, 2.4]}
                ticks={[0, 0.55, 1.1, 1.65, 2.2]}
                width={62}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="l1" name="L1 Bid" barSize={90} radius={[0, 0, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                fill={entry.isProjection ? '#0D9488' : '#0D9488'}
                  />
                ))}
              </Bar>
              <Line
                type="monotone"
                dataKey="trend"
                name="Trend"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={<CustomDot />}
                activeDot={{ r: 6, fill: '#3b82f6' }}
              />
            </ComposedChart>
          </ResponsiveContainer>

          {/* AI Recommendation Banner */}
          <div style={{
            marginTop: 20,
            background: '#ccfbf1',
            border: '1px solid #99f6e4',
            borderRadius: 8,
            padding: '12px 16px',
            fontSize: 13,
            color: '#0f766e',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span style={{ fontSize: 14 }}>✦</span>
            <span>
              AI Recommendation: Based on historical data, optimal bid range is{' '}
              <strong>₹1.84 Cr – ₹1.96 Cr</strong> for highest chance of winning.
            </span>
          </div>
        </div>

        {/* Recommendation Summary Card */}
        <div style={{
          flex: '1 1 30%',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: '24px 20px',
       height:'fit-content'
        }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', marginBottom: 16 }}>Recommendation Summary</div>

          <SummaryRow label="Historical Avg L1" value="₹2.09 Cr" />
          <SummaryRow label="Projected L1 (2025)" value="₹1.92 Cr" highlighted />
          <SummaryRow label="Optimal Bid Low" value="₹1.84 Cr" />
          <SummaryRow label="Optimal Bid High" value="₹1.96 Cr" />
          <SummaryRow label="Estimated Competition" value="12 bidders" />
          <SummaryRow label="Win Probability" value="41.8%" />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '13px 14px 0',
          }}>
            <span style={{ fontSize: 13, color: '#6b7280' }}>Reference Tender</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Similar: ₹2.2Cr - BEL</span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Projection;